import { useMemo } from "react";
import vCheck from "../../../../icons/vCheck.svg";
import * as translator from "../../../../utils/translator";
import { useCartContext } from "../../../../customHooks/useCart";
import { useShippingPageContext } from "../providers/ShippingPageProvider";

const CouponWidgetV2: React.FC = () => {
    const { cartCurrency, getCart } = useCartContext();
    const { language, couponWidgetState, setCouponWidgetState, handleApplyCoupon, expressDeliveryState } = useShippingPageContext();
    const cart = getCart();
    
    const isCouponConfirmed = useMemo(() => {
        return cart.bonus_value && (cart.bonus_value > 0) && !cart.auto_bonus
    }, [cart.bonus_value])


    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCouponWidgetState((current) => ({
            ...current,
            inputCode: e.target.value,
            errorMsg: "",
        }));
    }

    const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        handleApplyCoupon({inputCode: couponWidgetState.inputCode, isExpressSelected: expressDeliveryState.isSelected, shouldOverrideCartBonuses: isCouponConfirmed ? false : true}); //If coupon is already confirmed, we dont want to override it
    };

    const handleChangeCouponClick = () => {
        //Reset the state
        setCouponWidgetState((current) => ({
            ...current,
            exposeExtraEnterContainer: !current.exposeExtraEnterContainer,
            inputCode: "",
            errorMsg: "",
        }));
    };


    const enterCouponView = useMemo(() => {
        let errorMessage = couponWidgetState.errorMsg;
        const {
            minimum_price_required = 0,
            treatment_fee_fixed = 0,
            service_fee_fixed = 0,
        } = couponWidgetState.additionalCouponData ?? {};
        if (minimum_price_required > 0) {
            errorMessage = errorMessage?.replace(":order_total_coupon", minimum_price_required.toString());
        } else if (treatment_fee_fixed > 0) {
            errorMessage = errorMessage?.replace(":treatment_fee", treatment_fee_fixed.toString());
        } else if (service_fee_fixed > 0) {
            errorMessage = errorMessage?.replace(":service_fee", service_fee_fixed.toString());
        }
        return (
            <>

                {/* Enter Coupon Container  */}
                <form
                    id="enter-coupon-widget-container"
                    onSubmit={handleOnSubmit}
                    style={couponWidgetState.errorMsg ? { border: "1px solid #ef4747" } : {}}
                    className="relative flex items-center justify-between bg-white h-14 w-full rounded-md px-[20px] pr-[95px] transition-all duration-200"
                >
                    {/* Coupon input field  */}
                    <input
                        id="coupon-widget-input"
                        value={couponWidgetState.inputCode}
                        className="text-[13px] h-full w-full border-none outline-none overflow-hidden pr-6"
                        onChange={handleOnChange}
                        maxLength={25}
                        type={"text"}
                        placeholder={translator.translate(language, "shipping", "CouponWidget/coupon_code_placeholder")}
                    />

                    {/* Loader & Submit Button  */}
                    {couponWidgetState.isSubmitting ?
                        <div id="loader-coupon-widget" className="flex items-center justify-center w-[67px] h-9 absolute right-[10px] top-[10px]">
                            <div style={{width: '1.45rem', height: '1.45rem', border: '2px solid transparent', borderTop: '2px solid #269C6E', borderRight: '2px solid #269C6E', borderRadius: '9999px'}} className="animate-spin"/>
                        </div>
                        :
                        <button
                            id="coupon-widget-submit-button"
                            style={{ border: "2px solid #269C6E" }}
                            className="absolute flex items-center justify-center w-fit min-w-[67px] text-[12px] font-bold text-[#269C6E] bg-transparent h-9 rounded-[3px] px-[10px] py-[10px] right-[10px] top-[10px] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={couponWidgetState.inputCode === "" || couponWidgetState.isSubmitting}
                            type="submit"
                        >
                            <p>{translator.translate(language, "shipping", "CouponWidget/coupon_activate")}</p>
                        </button>
                    }

                </form>

                {/* Error Message  */}
                {errorMessage ? <span id="coupon-widget-error-message" className="text-[12px] w-fit my-1 font-medium text-[#ef4747]"> {errorMessage} </span> : null}
            </>
        )

    }, [couponWidgetState.inputCode , couponWidgetState.errorMsg, couponWidgetState.isSubmitting]);


    const couponConfirmedView = useMemo(() => {
        return (
            <>
                {/* Coupon Confirmed Container  */}
                <div
                    id="confirmed-coupon-widget-container"
                    style={{ border: "1px solid var(--primary-lightgreen)" }}
                    className="relative flex items-center justify-between bg-transparent h-14 w-full rounded-md px-5 transition-all duration-200 text-[13px] font-medium text-[var(--primary-lightgreen)] cursor-default"
                >
                    <p>- {translator.getPrice(cart.bonus_value, cartCurrency.locale, cartCurrency.rate)}</p>
                    <img className="coupon-confirmed-vCheck" src={vCheck} alt="check" />
                </div>


                {/* Change Coupon button  */}
                <p
                    onClick={handleChangeCouponClick}
                    className="coupon-confirmed-change-coupon"
                >
                    {translator.translate(language, "shipping", "CouponWidget/change_coupon")}
                </p>
            </>
        )

    }, [cartCurrency.locale, cartCurrency.rate, cart.bonus_value])


    // Hide if:
    if (cart.auto_bonus || cart.applied_subscription_discount) {
        return <></>
    }


    return (
        <>  
            {/* Title  */}
            <span id='coupon-widget-title' className='font-semibold font-roboto text-[#98a0af] text-[13px] my-[15px] block'>
                {translator.translate(language, "shipping", "CouponWidget/title")}
            </span>

            {/* Containers */}
            {isCouponConfirmed ? couponConfirmedView : enterCouponView}
            {couponWidgetState.exposeExtraEnterContainer && isCouponConfirmed ? enterCouponView : null}
        </>
    );
};

export default CouponWidgetV2;